import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Animated,
  Easing,
  Image,
  ImageBackground,
  useWindowDimensions,
  Platform,
  Modal,
  TouchableWithoutFeedback,
  Keyboard,
  InputAccessoryView,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, AlertTriangle, HelpCircle, ChevronDown, ChevronUp, X, LockKeyhole } from 'lucide-react-native';
import { format } from 'date-fns';
import { getCurrentTime } from '../utils/timeUtils';
import { useTRPC, useTRPCClient } from '../lib/trpc';
import { isUnauthorizedError } from '../lib/authUtils';
import { useToast } from '../hooks/useToast';
import { Card, CardContent } from './ui/Card';
import { Box, Text, PressableBox } from './ui/primitives';
import { Input } from './ui/Input';
import { MatchupAnalysisPanel } from './MatchupAnalysisPanel';
import { GameCardScoreboard, type GameScoreboardPickState } from './GameCardScoreboard';
import { colors, animations } from '../lib/theme';
import { tokens } from '../../design-tokens';
import { getContrastingTextColor, getTeamPrimaryColor } from '../lib/teamColors';
import { getTeamBranding } from '../lib/teamBranding';
import { resolveAvatarUri } from './ui/Avatar';
import { PICK_LOCKED_ERROR } from '@shared/pickLocking';
import {
  formatPointsLabel,
  getBetPoints,
  inferBetSideFromSelection,
  resolveWinningSide,
  type LegacyScoringSystem,
  type BetSide,
} from '@/lib/scoring';

const platformLetterSpacingStyle = { letterSpacing: Platform.OS === 'ios' ? 0 : 0.25 };

interface Team {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  record?: string | null;
}

type MatchupRecordData = {
  awayTeam?: { record?: { overall?: string | null } | null };
  homeTeam?: { record?: { overall?: string | null } | null };
};

interface Game {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  homeScore?: number;
  awayScore?: number;
  homeLineScores?: unknown;
  awayLineScores?: unknown;
  gameTime: string;
  isCompleted: boolean;
  winnerId?: string | null;
  homeOdds?: string | null;
  awayOdds?: string | null;
  network?: string;
  period?: string | null;
  clock?: string | null;
  situation?: string | null;
}

interface GameBetEntry {
  bet: {
    betType: 'moneyline' | 'spread' | 'total' | 'total_tiebreaker' | 'custom_prop';
    oddsOne?: number | null;
    oddsTwo?: number | null;
    displayInfo?: {
      options?: {
        label: string;
        odds: number;
        side: string;
        playerId?: string;
        teamAbbr?: string;
        position?: string;
        theme?: {
          backgroundColor?: string;
          textColor?: string;
          accentColor?: string;
          backgroundImagePath?: string;
        };
      }[],
      [key: string]: unknown;
    } | null;
    leagueWeekBetId?: string | null;
    cutoffAt?: string | null;
    pickLockCutoffAt?: string | null;
    locked?: boolean;
    scoringConfigOverride?: Record<string, unknown> | null;
  };
  betKey: string;
  selectedTeamId?: string;
  pickId?: string;
  pick?: { details?: { tiebreakerGuess?: number } | null } | null;
  side?: string; // Add side to track selection for custom props
}

interface GameCardProps {
  game: Game;
  betEntries: GameBetEntry[];
  cardTitle?: string;
  hideGameTeams?: boolean;
  propsSectionTitle?: string;
  propsInitiallyExpanded?: boolean;
  isFeatured?: boolean;
  isGameStartedOverride?: boolean;
  afterPickContent?: React.ReactNode;
  pick?: { details?: { tiebreakerGuess?: number } | null } | null;
  leagueId: string;
  weekId?: string | null;
  seasonId?: string | null;
  picksQueryKey: readonly unknown[];
  scoringSystem?: LegacyScoringSystem;
  seasonWeekMode?: 'standard' | 'propWeeklyOnly';
  adminUserId?: string;
  onTiebreakerDirtyChange?: (betKey: string, isDirty: boolean) => void;
}

type SubmitPayload = {
  betEntry: GameBetEntry;
  teamId: string;
  side?: string | undefined;
  tiebreakerGuess?: number;
  previousSelection?: string | undefined;
};

type PickShape = {
  id: string;
  leagueWeekBetId?: string | null;
  betType?: string;
  gameId: string;
  selectedTeamId: string;
};

type BetOption = {
  key: string;
  label: string;
  value: string;
  teamId: string;
  odds: number | string | null | undefined;
  side: BetSide;
  playerId?: string;
  position?: string;
  teamAbbr?: string;
  theme?: {
    backgroundColor?: string;
    textColor?: string;
    accentColor?: string;
    backgroundImagePath?: string;
  };
};

const derivePickKey = (pick: PickShape) => pick.leagueWeekBetId ?? `${pick.gameId}-${pick.betType ?? 'moneyline'}`;

const formatOdds = (odds?: number | string | null) => {
  if (odds === undefined || odds === null) return 'EVEN';
  const parsed = typeof odds === 'string' ? parseFloat(odds) : odds;
  if (!Number.isFinite(parsed)) return 'EVEN';
  return parsed > 0 ? `+${parsed}` : `${parsed}`;
};

const formatSpread = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  if (value > 0) return `+${value}`;
  return `${value}`;
};

const parseDisplayNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatTotal = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  return `${value}`;
};

const TeamBadge = ({
  team,
  size,
  muted = false,
}: {
  team: Team;
  size: number;
  muted?: boolean;
}) => {
  const branding = getTeamBranding(team);

  if (branding.logoUrl) {
    const imageStyle = {
      height: size,
      width: size,
      backgroundColor: tokens.colors.ink.paperDark,
      borderRadius: 0,
      borderColor: tokens.colors.ink.press,
      borderWidth: 2,
      opacity: muted ? 0.58 : 1,
    };
    return (
      <Image
        source={{ uri: branding.logoUrl }}
        resizeMode="contain"
        style={imageStyle}
      />
    );
  }

  const badgeBackground = branding.secondaryColor ?? tokens.colors.neutral[100];
  const badgeBorder = branding.primaryColor ?? tokens.colors.neutral[300];
  const badgeStyle = {
    height: size,
    width: size,
    borderRadius: 0,
    backgroundColor: badgeBackground,
    borderColor: badgeBorder,
    borderWidth: 2,
    opacity: muted ? 0.58 : 1,
  };
  const fallbackTextStyle = {
    fontSize: Math.round(size * 0.55),
    lineHeight: Math.round(size * 0.65),
  };

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      style={badgeStyle}
    >
      <Text
        style={fallbackTextStyle}
      >
        {branding.flagEmoji ?? team.abbreviation.slice(0, 3)}
      </Text>
    </Box>
  );
};

const WorldCupFlagBackdrop = ({
  flagImageUrl,
  primaryColor,
}: {
  flagImageUrl: string | null;
  primaryColor: string;
}) => (
  <Box pointerEvents="none" style={styles.worldCupFlagBackdrop}>
    {flagImageUrl ? (
      <ImageBackground source={{ uri: flagImageUrl }} resizeMode="cover" style={styles.worldCupFlagImage} />
    ) : (
      <Box style={[styles.worldCupFlagSolidFallback, { backgroundColor: primaryColor }]} />
    )}
    <Box style={styles.worldCupFlagImageTint} />
  </Box>
);

const parseOddsValue = (odds?: number | string | null): number | null => {
  if (odds === undefined || odds === null) return null;
  const parsed = typeof odds === 'string' ? parseFloat(odds) : odds;
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const formatImpliedProbability = (odds?: number | string | null) => {
  const parsed = parseOddsValue(odds);
  if (parsed === null) return '—';
  const probability = parsed > 0 ? 100 / (parsed + 100) : Math.abs(parsed) / (Math.abs(parsed) + 100);
  return `${Math.round(probability * 100)}%`;
};

const buildBetLabel = (betType: string, displayInfo?: Record<string, unknown> | null) => {
  if (betType === 'moneyline' && displayInfo?.marketSubtype === 'soccer_to_advance') {
    return 'To Advance';
  }
  switch (betType) {
    case 'spread':
      return 'Against the Spread';
    case 'total':
      return 'Game Total';
    case 'total_tiebreaker':
      return 'Total Tiebreaker';
    case 'custom_prop': {
      if (displayInfo?.propSubtype === 'soccer_regulation_moneyline') {
        return 'Regulation Moneyline';
      }
      const label = typeof displayInfo?.label === 'string' ? displayInfo.label.trim() : '';
      return label || 'Custom Prop';
    }
    default:
      return 'Moneyline';
  }
};

const getBetHelpText = (betType: string) => {
  switch (betType) {
    case 'moneyline':
      return "Pick the game winner. Negative odds show how much you need to bet to win $100; positive odds show how much you win on a $100 bet.";
    case 'spread':
      return "The favorite must win by more than the spread. The underdog can win outright or lose by less than the spread.";
    case 'total':
      return "Bet on if the total number of points scored in the game will be over or under the listed number of points.";
    case 'total_tiebreaker':
      return "Enter a total points guess. The closest number wins the tiebreaker when scores are tied.";
    case 'custom_prop':
      return "A special bet with its own win conditions, described in the bet details.";
    default:
      return "Pick the winner based on the bet’s listed conditions.";
  }
};

const splitPointsLabel = (label: string) => {
  const trimmed = label.trim();
  if (!trimmed) return { value: '', unit: '' };
  const parts = trimmed.split(' ');
  if (parts.length <= 1) return { value: trimmed, unit: '' };
  const unit = parts.pop() ?? '';
  return { value: parts.join(' '), unit };
};

const textureSources = {
  grassNoise: require('../../assets/textures/grass-noise.jpg'),
  paintWear: require('../../assets/textures/paint-wear.jpg'),
  sprayDark: require('../../assets/textures/spray-dark.jpg'),
  sprayEdgeDark: require('../../assets/textures/spray-edge-dark.jpg'),
  sprayEdgeLight: require('../../assets/textures/spray-edge-light.jpg'),
  sprayLight: require('../../assets/textures/spray-light.jpg'),
};

const styles = StyleSheet.create(() => ({
  atBadge: {
    backgroundColor: tokens.colors.ink.paper,
    borderColor: tokens.colors.ink.press,
    borderWidth: 2,
  },
  betOptionGroup: {
    position: 'relative',
  },
  correctTeamResult: {
    borderColor: tokens.colors.state.success,
  },
  featuredCardSurface: {
    backgroundColor: tokens.colors.ink.paper,
    borderColor: tokens.colors.neutral[300],
    borderRadius: 4,
    borderWidth: 1,
    elevation: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  featuredMasthead: {
    backgroundColor: tokens.colors.primary[700],
    borderColor: tokens.colors.warning[300],
    borderWidth: 2,
    overflow: 'hidden',
    paddingHorizontal: 20,
    paddingVertical: 18,
    position: 'relative',
  },
  featuredMastheadContent: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  featuredMastheadEyebrow: {
    color: tokens.colors.warning[400],
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 2.4,
    lineHeight: 20,
    marginBottom: 2,
  },
  featuredMastheadFrame: {
    borderColor: tokens.colors.warning[300],
    borderWidth: 1,
    bottom: 7,
    left: 7,
    position: 'absolute',
    right: 7,
    top: 7,
  },
  featuredMastheadQuestion: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.7,
    lineHeight: 20,
    marginTop: 7,
    textTransform: 'uppercase',
  },
  featuredMastheadStripe: {
    backgroundColor: tokens.colors.warning[300],
    height: 170,
    opacity: 0.12,
    position: 'absolute',
    right: 48,
    top: -12,
    transform: [{ rotate: '24deg' }],
    width: 20,
  },
  featuredMastheadStripeSecondary: {
    opacity: 0.08,
    right: 12,
  },
  featuredMastheadTitle: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 38,
    letterSpacing: -1.4,
    lineHeight: 43,
    maxWidth: '100%',
    textTransform: 'uppercase',
  },
  featuredMastheadTitleExtraLong: {
    fontSize: { xs: 24, md: 30 },
    letterSpacing: -0.6,
    lineHeight: { xs: 28, md: 34 },
  },
  featuredMastheadTitleLong: {
    fontSize: { xs: 30, md: 34 },
    letterSpacing: -1,
    lineHeight: { xs: 34, md: 38 },
  },
  featuredOptionArtBackground: {
    bottom: 0,
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  featuredOptionArtScrim: {
    backgroundColor: tokens.colors.ink.press,
    bottom: 0,
    left: 0,
    opacity: 0.52,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  featuredOptionArtTint: {
    bottom: 0,
    left: 0,
    opacity: 0.3,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  featuredOptionContent: {
    alignItems: 'stretch',
    flexDirection: 'row',
    minHeight: 88,
    paddingHorizontal: 16,
    paddingVertical: 6,
    position: 'relative',
    zIndex: 1,
  },
  featuredOptionContentUnselected: {
    opacity: 0.85,
  },
  featuredOptionInfo: {
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  featuredOptionMeta: {
    color: tokens.colors.ink.pressMuted,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 13,
    letterSpacing: 0.4,
    lineHeight: 19,
    marginTop: 3,
    textTransform: 'uppercase',
  },
  featuredOptionMetaSelected: {
    color: tokens.colors.primary[100],
  },
  featuredOptionName: {
    color: tokens.colors.ink.press,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 24,
    letterSpacing: -0.6,
    lineHeight: 28,
    textTransform: 'uppercase',
  },
  featuredOptionNameCompact: {
    fontSize: 19,
    lineHeight: 23,
  },
  featuredOptionNameSelected: {
    color: tokens.colors.ink.paper,
  },
  featuredOptionOdds: {
    color: tokens.colors.ink.pressMuted,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
  },
  featuredOptionOddsSelected: {
    color: tokens.colors.primary[100],
  },
  featuredOptionPoints: {
    alignItems: 'flex-start',
    borderLeftColor: tokens.colors.neutral[400],
    borderLeftWidth: 3,
    justifyContent: 'center',
    marginLeft: 8,
    minWidth: 82,
    paddingLeft: 8,
  },
  featuredOptionPointsSelected: {
    borderLeftColor: tokens.colors.primary[200],
  },
  featuredOptionPointsUnit: {
    color: tokens.colors.ink.press,
    fontFamily: tokens.typography.fontFamily.bodyMedium,
    fontSize: 15,
  },
  featuredOptionPointsUnitSelected: {
    color: tokens.colors.ink.paper,
  },
  featuredOptionPointsValue: {
    color: tokens.colors.ink.press,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 30,
    letterSpacing: -1,
    lineHeight: 34,
  },
  featuredOptionPointsValueSelected: {
    color: tokens.colors.ink.paper,
  },
  featuredOptionProbability: {
    color: tokens.colors.ink.pressMuted,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    lineHeight: 16,
    marginTop: 3,
  },
  featuredOptionProbabilitySelected: {
    color: tokens.colors.primary[100],
  },
  featuredOptionSelectedShadow: {
    elevation: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  featuredOptionShadow: {
    elevation: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  featuredOptionSurface: {
    backgroundColor: tokens.colors.ink.paper,
    borderColor: tokens.colors.neutral[300],
    borderWidth: 1,
    minHeight: 94,
    overflow: 'hidden',
    position: 'relative',
  },
  featuredOptionSurfaceSelected: {
    backgroundColor: tokens.colors.primary[700],
    borderColor: tokens.colors.warning[300],
    borderWidth: 2,
  },
  featuredOptionUnselectedWash: {
    backgroundColor: tokens.colors.neutral[200],
    bottom: 0,
    left: 0,
    opacity: 0.72,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  featuredPropDescription: {
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamily.bodyMedium,
    fontSize: tokens.typography.fontSize.md,
    fontWeight: '700',
    lineHeight: 24,
  },
  featuredPropLabel: {
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamily.bodyMedium,
    fontSize: tokens.typography.fontSize.base,
    fontWeight: '700',
  },
  featuredTeamColorField: {
    opacity: 0.18,
  },
  featuredTeamLogoImage: {
    opacity: 0.2,
  },
  featuredTeamLogoTint: {
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
  gameCardSurface: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderWidth: 0,
    elevation: 0,
    overflow: 'hidden',
    position: 'relative',
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  gameCardSurfaceLocked: {
    borderColor: tokens.colors.neutral[300],
  },
  gameTimeLabel: {
    color: tokens.colors.ink.press,
    flexShrink: 1,
    fontFamily: 'Oswald_600SemiBold',
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.4,
    lineHeight: 18,
  },
  incorrectTeamResult: {
    borderColor: tokens.colors.state.error,
  },
  pickRequiredBanner: {
    alignItems: 'center',
    backgroundColor: tokens.colors.state.warningBg,
    borderColor: tokens.colors.state.warningBorder,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  pickRequiredText: {
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamily.body,
    fontSize: 17,
    fontWeight: '600',
    lineHeight: 22,
  },
  selectedTeamBackdrop: {
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  selectedTeamColorField: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  selectedTeamHighlight: {
    position: 'absolute',
    zIndex: 2,
  },
  selectedTeamHighlightBottom: {
    bottom: 0,
  },
  selectedTeamHighlightHorizontal: {
    height: 4,
    left: 0,
    right: 0,
  },
  selectedTeamHighlightLeft: {
    left: 0,
  },
  selectedTeamHighlightRight: {
    right: 0,
  },
  selectedTeamHighlightTop: {
    top: 0,
  },
  selectedTeamHighlightVertical: {
    bottom: 0,
    top: 0,
    width: 4,
  },
  selectedTeamLogoImage: {
    bottom: 0,
    left: 0,
    opacity: 0.5,
    position: 'absolute',
    right: 0,
    top: 0,
    transform: [{ scale: 2.35 }],
  },
  selectedTeamLogoImageHighContrast: {
    opacity: 0.7,
  },
  selectedTeamLogoTint: {
    backgroundColor: 'rgba(22, 36, 28, 0.2)',
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  selectedTeamResultBadge: {
    alignItems: 'center',
    borderColor: tokens.colors.ink.paper,
    borderRadius: 16,
    borderWidth: 2,
    height: 28,
    justifyContent: 'center',
    position: 'absolute',
    right: 8,
    top: 8,
    width: 28,
    zIndex: 4,
  },
  selectedTeamResultBadgeCorrect: {
    backgroundColor: tokens.colors.state.success,
  },
  selectedTeamResultBadgeIncorrect: {
    backgroundColor: tokens.colors.state.error,
  },
  selectedTeamTextOutline: {
    textShadowColor: 'rgba(22, 36, 28, 0.98)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 1.5,
  },
  sprayedChalkLayer: {
    opacity: 1,
  },
  sprayedChalkTexture: {
    bottom: 0,
    height: '100%',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
  },
  teamPickOptionPressable: {
    flexGrow: 1,
  },
  teamPickOptionSurface: {
    flexGrow: 1,
  },
  unselectedTeamLogoImage: {
    opacity: 0.22,
  },
  unselectedTeamLogoTint: {
    backgroundColor: 'rgba(226, 232, 222, 0.7)',
  },
  worldCupFlagBackdrop: {
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  worldCupFlagImage: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  worldCupFlagImageTint: {
    backgroundColor: 'rgba(22, 36, 28, 0.24)',
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  worldCupFlagMutedTint: {
    backgroundColor: 'rgba(226, 232, 222, 0.76)',
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  worldCupFlagSolidFallback: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  worldCupTextShadow: {
    textShadowColor: 'rgba(22, 36, 28, 0.88)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 0,
  },
}));

const BetHelpButton = ({ betType }: { betType: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const triggerRef = useRef<any>(null);
  const helpText = getBetHelpText(betType);
  const tooltipTriggerStyle = { position: 'relative' as const, zIndex: 45 };
  const webOverlayStyle = {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 40,
    cursor: 'default' as const,
  } as any;
  const webTooltipStyle = {
    backgroundColor: colors.ink.paper,
    transform: [{ translateX: -96 }],
    shadowColor: colors.ink.press,
    shadowOffset: { width: 6, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 0,
  };
  const webArrowStyle = {
    marginTop: -4,
    backgroundColor: colors.ink.paper,
    transform: [{ translateX: -4 }, { rotate: '45deg' }],
  };
  const mobileOverlayStyle = { backgroundColor: 'rgba(22, 36, 28, 0.72)' };
  const mobileTooltipStyle = {
    backgroundColor: colors.ink.paper,
    top: coords.y - 8,
    left: coords.x + coords.width / 2,
    transform: [{ translateX: -96 }, { translateY: -100 }],
    shadowColor: colors.ink.press,
    shadowOffset: { width: 6, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 0,
  };
  const mobileArrowStyle = {
    marginTop: -4,
    backgroundColor: colors.ink.paper,
    transform: [{ translateX: -4 }, { rotate: '45deg' }],
  };
  const helpTextStyle = { lineHeight: 16 };

  const handlePress = () => {
    if (Platform.OS === 'web') {
      setShowTooltip((prev) => !prev);
      return;
    }
    triggerRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setCoords({ x, y, width, height });
      setModalVisible(true);
    });
  };

  return (
    <Box position="relative" zIndex={50}>
      {/* Web Tooltip Overlay */}
      {showTooltip && Platform.OS === 'web' && (
        <PressableBox
          onPress={() => setShowTooltip(false)}
          style={webOverlayStyle} // Cast entire style to any to avoid Web/Native prop conflicts
        />
      )}

      <PressableBox
        ref={triggerRef}
        hitSlop={8}
        onPress={handlePress}
        // @ts-ignore
        onHoverIn={() => Platform.OS === 'web' && setShowTooltip(true)}
        // @ts-ignore
        onHoverOut={() => Platform.OS === 'web' && setShowTooltip(false)}
        style={Platform.OS === 'web' ? tooltipTriggerStyle : undefined}
      >
        <HelpCircle size={14} color={colors.ink.pressMuted} />
      </PressableBox>

      {/* Web Tooltip Content */}
      {showTooltip && Platform.OS === 'web' && (
        <Box 
          position="absolute" 
          bottom="100%" 
          left="50%" 
          marginBottom="2" 
          width={192} 
          padding="2" 
          borderRadius="none" 
          borderWidth={3}
          borderColor="press"
          zIndex={50} 
          style={webTooltipStyle}
        >
           {/* Triangle arrow - manually styled */}
          <Box 
            position="absolute" 
            top="100%" 
            left="50%" 
            width={8} 
            height={8} 
            style={webArrowStyle}
          />
          <Text color="press" variant="caption" textAlign="center" style={helpTextStyle}>{helpText}</Text>
        </Box>
      )}

      {/* Mobile Modal Tooltip */}
      {Platform.OS !== 'web' && (
        <Modal
          transparent={true}
          visible={modalVisible}
          animationType="fade"
          onRequestClose={() => setModalVisible(false)}
        >
          <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
            <Box flex={1} style={mobileOverlayStyle}>
              <TouchableWithoutFeedback>
                <Box
                  position="absolute"
                  padding="2"
                  borderRadius="none"
                  borderWidth={3}
                  borderColor="press"
                  width={192}
                  style={mobileTooltipStyle}
                >
                  <Text color="press" variant="caption" textAlign="center" style={helpTextStyle}>{helpText}</Text>
                  <Box 
                    position="absolute" 
                    top="100%" 
                    left="50%" 
                    width={8} 
                    height={8} 
                    style={mobileArrowStyle}
                  />
                </Box>
              </TouchableWithoutFeedback>
            </Box>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </Box>
  );
};

const PaintedTurfTexture = React.memo(function PaintedTurfTexture({
  edge = false,
  intensity = 'surface',
  tone = 'light',
}: {
  edge?: boolean;
  intensity?: 'surface' | 'pick';
  tone?: 'dark' | 'light';
}) {
  const isDark = tone === 'dark';
  const isPick = intensity === 'pick';

  return (
    <Box
      pointerEvents="none"
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      overflow="hidden"
      style={styles.sprayedChalkLayer}
    >
      <Image
        source={isDark ? textureSources.sprayDark : textureSources.sprayLight}
        fadeDuration={0}
        resizeMode="repeat"
        style={[
          styles.sprayedChalkTexture,
          { opacity: isDark ? (isPick ? 0.36 : 0.12) : (isPick ? 0.34 : 0.32) },
        ]}
      />
      <Image
        source={textureSources.grassNoise}
        fadeDuration={0}
        resizeMode="repeat"
        style={[
          styles.sprayedChalkTexture,
          { opacity: isDark ? (isPick ? 0.22 : 0.08) : (isPick ? 0.16 : 0.12) },
        ]}
      />
      <Image
        source={textureSources.paintWear}
        fadeDuration={0}
        resizeMode="repeat"
        style={[
          styles.sprayedChalkTexture,
          { opacity: isDark ? (isPick ? 0.16 : 0.06) : (isPick ? 0.14 : 0.12) },
        ]}
      />
      {edge ? (
        <Image
          source={isDark ? textureSources.sprayEdgeDark : textureSources.sprayEdgeLight}
          fadeDuration={0}
          resizeMode="stretch"
          style={[
            styles.sprayedChalkTexture,
            { opacity: isDark ? (isPick ? 0.28 : 0.18) : (isPick ? 0.22 : 0.16) },
          ]}
        />
      ) : null}
    </Box>
  );
});

// Long featured titles step down in size so they don't dominate the picks screen on phones.
const FEATURED_TITLE_LONG_LENGTH = 32;
const FEATURED_TITLE_EXTRA_LONG_LENGTH = 60;

const FeaturedPickMasthead = React.memo(function FeaturedPickMasthead({
  title,
  description,
}: {
  title: string;
  description?: string | null;
}) {
  const titleLength = title.trim().length;
  const titleSizeStyle =
    titleLength > FEATURED_TITLE_EXTRA_LONG_LENGTH
      ? styles.featuredMastheadTitleExtraLong
      : titleLength > FEATURED_TITLE_LONG_LENGTH
        ? styles.featuredMastheadTitleLong
        : undefined;
  return (
    <Box style={styles.featuredMasthead} accessibilityRole="header">
      <PaintedTurfTexture intensity="pick" tone="dark" />
      <Box pointerEvents="none" style={styles.featuredMastheadFrame} />
      <Box pointerEvents="none" style={styles.featuredMastheadStripe} />
      <Box pointerEvents="none" style={[styles.featuredMastheadStripe, styles.featuredMastheadStripeSecondary]} />
      <Box position="relative" zIndex={1} style={styles.featuredMastheadContent}>
        <Text style={styles.featuredMastheadEyebrow}>PICK OF THE WEEK</Text>
        <Text style={[styles.featuredMastheadTitle, titleSizeStyle]}>
          {title}
        </Text>
        {description ? (
          <Text style={styles.featuredMastheadQuestion}>
            {description}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
});

const SelectedTeamBackdrop = React.memo(function SelectedTeamBackdrop({
  logoUrl,
  logoTintColor,
  primaryColor,
  muted = false,
  tone = 'solid',
}: {
  logoUrl?: string | null;
  logoTintColor?: string | null;
  primaryColor: string;
  muted?: boolean;
  tone?: 'solid' | 'tinted';
}) {
  return (
    <Box pointerEvents="none" style={styles.selectedTeamBackdrop}>
      <Box
        pointerEvents="none"
        style={[
          styles.selectedTeamColorField,
          tone === 'tinted' && styles.featuredTeamColorField,
          { backgroundColor: primaryColor },
        ]}
      />
      {logoUrl ? (
        <Image
          source={{ uri: logoUrl }}
          resizeMode="contain"
          tintColor={logoTintColor ?? undefined}
          style={[
            styles.selectedTeamLogoImage,
            logoTintColor ? styles.selectedTeamLogoImageHighContrast : undefined,
            tone === 'tinted' && styles.featuredTeamLogoImage,
            muted && styles.unselectedTeamLogoImage,
          ]}
        />
      ) : null}
      {logoUrl ? (
        <Box
          pointerEvents="none"
          style={[
            styles.selectedTeamLogoTint,
            tone === 'tinted' && styles.featuredTeamLogoTint,
            muted && styles.unselectedTeamLogoTint,
          ]}
        />
      ) : null}
    </Box>
  );
});

type PickOutcome = 'win' | 'loss' | 'push';

const normalizeTiebreakerValue = (value?: string | number | null) => {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'number' ? String(value) : value.trim();
  if (!raw) return '';
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) return raw;
  return String(parsed);
};

export default function GameCard({
  game,
  betEntries,
  cardTitle,
  hideGameTeams = false,
  propsSectionTitle = 'Prop Bets',
  propsInitiallyExpanded = false,
  isFeatured = false,
  isGameStartedOverride,
  afterPickContent,
  leagueId,
  weekId,
  seasonId,
  picksQueryKey,
  scoringSystem = 'oddsBased',
  adminUserId,
  onTiebreakerDirtyChange,
}: GameCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const { width } = useWindowDimensions();
  const matchupAnalysisQuery = useQuery(
    trpc.nfl.matchupAnalysis.queryOptions(
      { gameId: game.id },
      {
        enabled: game.id.startsWith('api-football-nfl:'),
        staleTime: 15 * 60 * 1000,
      },
    ),
  );
  const matchupAnalysis = matchupAnalysisQuery.data as MatchupRecordData | undefined;
  const [localSelections, setLocalSelections] = useState<Record<string, string | undefined>>({});
  const [pressedTeamOptionKey, setPressedTeamOptionKey] = useState<string | null>(null);
  const teamOptionPressAnim = useRef(new Animated.Value(0)).current;
  const pressedTeamOptionRef = useRef<string | null>(null);
  const [tiebreakerInputs, setTiebreakerInputs] = useState<Record<string, string>>({});
  const [isPropsExpanded, setIsPropsExpanded] = useState(propsInitiallyExpanded);
  const pendingSelectionsRef = useRef<Map<string, string>>(new Map());
  const pendingTiebreakerRef = useRef<Map<string, string>>(new Map());
  const tiebreakerDirtyRef = useRef<Map<string, boolean>>(new Map());
  const tiebreakerLastSubmittedRef = useRef<Map<string, string>>(new Map());
  // React Native Web has no native animated module. Rendering every card at
  // full opacity on web avoids starting a JS animation for the entire slate.
  const fadeAnim = useRef(new Animated.Value(Platform.OS === 'web' ? 1 : 0)).current;
  const isCompactLayout = width <= 400;
  const isWideLayout = width >= 600;
  const teamLogoSize = isCompactLayout ? 28 : 32;
  const teamPickLogoSize = isCompactLayout ? 56 : 64;
  const featuredPropTitle = useMemo(() => {
    if (!isFeatured) return null;
    const featuredBet = betEntries.find((entry) => entry.bet.betType === 'custom_prop');
    const label = featuredBet?.bet.displayInfo?.label;
    return typeof label === 'string' && label.trim() ? label.trim() : null;
  }, [betEntries, isFeatured]);
  const featuredPropDescription = useMemo(() => {
    if (!isFeatured) return null;
    const featuredBet = betEntries.find((entry) => entry.bet.betType === 'custom_prop');
    const description = featuredBet?.bet.displayInfo?.description;
    return typeof description === 'string' && description.trim() ? description.trim() : null;
  }, [betEntries, isFeatured]);
  const normalizedGameOdds = useMemo(
    () => ({
      homeOdds: game.homeOdds ?? null,
      awayOdds: game.awayOdds ?? null,
    }),
    [game.homeOdds, game.awayOdds],
  );
  const matchupMarkets = useMemo(
    () => betEntries.flatMap(({ bet }) => {
      if (bet.betType !== 'moneyline' && bet.betType !== 'spread' && bet.betType !== 'total') return [];
      return [{
        type: bet.betType,
        oddsOne: bet.oddsOne ?? null,
        oddsTwo: bet.oddsTwo ?? null,
        spread: parseDisplayNumber(bet.displayInfo?.spread),
        total: parseDisplayNumber(bet.displayInfo?.total),
      }];
    }),
    [betEntries],
  );
  const handleTeamOptionPressIn = useCallback((teamOptionKey: string) => {
    pressedTeamOptionRef.current = teamOptionKey;
    setPressedTeamOptionKey(teamOptionKey);
    teamOptionPressAnim.stopAnimation();
    Animated.timing(teamOptionPressAnim, {
      toValue: 1,
      duration: 110,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [teamOptionPressAnim]);

  const handleTeamOptionPressOut = useCallback((teamOptionKey: string) => {
    if (pressedTeamOptionRef.current !== teamOptionKey) return;
    Animated.timing(teamOptionPressAnim, {
      toValue: 0,
      duration: 180,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start(({ finished }) => {
      if (finished && pressedTeamOptionRef.current === teamOptionKey) {
        pressedTeamOptionRef.current = null;
        setPressedTeamOptionKey(null);
      }
    });
  }, [teamOptionPressAnim]);

  const propStats = useMemo(() => {
    const props = betEntries.filter((e) => ['total', 'custom_prop'].includes(e.bet.betType));
    const total = props.length;
    let made = 0;
    let correct = 0;

    props.forEach((entry) => {
      const selection = localSelections[entry.betKey];
      if (selection) {
        made++;
        const winningSide = resolveWinningSide(entry.bet, {
          homeTeam: game.homeTeam,
          awayTeam: game.awayTeam,
          homeScore: typeof game.homeScore === 'number' ? game.homeScore : null,
          awayScore: typeof game.awayScore === 'number' ? game.awayScore : null,
          winnerId: game.winnerId,
        });

        const isMultiOption =
          entry.bet.betType === 'custom_prop' &&
          entry.bet.displayInfo?.options &&
          Array.isArray(entry.bet.displayInfo.options) &&
          entry.bet.displayInfo.options.length > 0;

        const side = isMultiOption
          ? (selection as BetSide)
          : inferBetSideFromSelection(game, selection);
        
        if (winningSide && side && winningSide === side) {
           if (entry.bet.betType === 'custom_prop' || game.isCompleted) {
             correct++;
           }
        }
      }
    });

    return { total, made, correct };
  }, [betEntries, localSelections, game]);

  useEffect(() => {
    setLocalSelections((prev) => {
      const next: Record<string, string | undefined> = { ...prev };
      betEntries.forEach((entry) => {
        const pendingValue = pendingSelectionsRef.current.get(entry.betKey);
        if (pendingValue) {
          next[entry.betKey] = pendingValue;
          if (entry.selectedTeamId === pendingValue) {
            pendingSelectionsRef.current.delete(entry.betKey);
          }
        } else {
          const isMultiOption =
            entry.bet.betType === 'custom_prop' &&
            entry.bet.displayInfo?.options &&
            Array.isArray(entry.bet.displayInfo.options) &&
            entry.bet.displayInfo.options.length > 0;

          next[entry.betKey] = isMultiOption
            ? (entry.side ?? entry.selectedTeamId)
            : entry.selectedTeamId;
        }
      });
      return next;
    });

    setTiebreakerInputs((prev) => {
      const next: Record<string, string> = { ...prev };
      betEntries.forEach((entry) => {
        if (entry.bet.betType !== 'total_tiebreaker') return;
        const pendingValue = pendingTiebreakerRef.current.get(entry.betKey);
        const existingGuess = entry.pick?.details?.tiebreakerGuess;
        const previousValue = prev[entry.betKey];
        if (pendingValue !== undefined) {
          next[entry.betKey] = pendingValue;
          if (typeof existingGuess === 'number' && String(existingGuess) === pendingValue) {
            pendingTiebreakerRef.current.delete(entry.betKey);
          }
        } else {
          const normalizedExisting = normalizeTiebreakerValue(existingGuess);
          const normalizedSubmitted =
            normalizeTiebreakerValue(tiebreakerLastSubmittedRef.current.get(entry.betKey));
          const normalizedPrevious = normalizeTiebreakerValue(previousValue);
          const isDirty = previousValue !== undefined && normalizedPrevious !== normalizedExisting;
          if (isDirty) {
            next[entry.betKey] = previousValue ?? '';
          } else if (typeof existingGuess === 'number') {
            next[entry.betKey] = normalizedExisting;
          } else if (normalizedSubmitted) {
            next[entry.betKey] = normalizedSubmitted;
          } else if (previousValue !== undefined) {
            next[entry.betKey] = previousValue;
          } else {
            next[entry.betKey] = '';
          }
        }
      });
      return next;
    });

    const validKeys = new Set(betEntries.map((entry) => entry.betKey));
    pendingSelectionsRef.current.forEach((_value, key) => {
      if (!validKeys.has(key)) {
        pendingSelectionsRef.current.delete(key);
      }
    });
    pendingTiebreakerRef.current.forEach((_value, key) => {
      if (!validKeys.has(key)) {
        pendingTiebreakerRef.current.delete(key);
      }
    });
  }, [betEntries]);

  useEffect(() => {
    if (!onTiebreakerDirtyChange) return;
    const activeKeys = new Set<string>();
    betEntries.forEach((entry) => {
      if (entry.bet.betType !== 'total_tiebreaker') return;
      activeKeys.add(entry.betKey);
      const existingGuess = entry.pick?.details?.tiebreakerGuess;
      const normalizedExisting = normalizeTiebreakerValue(existingGuess);
      const fallbackSubmitted = normalizeTiebreakerValue(
        tiebreakerLastSubmittedRef.current.get(entry.betKey),
      );
      const baseline = normalizedExisting || fallbackSubmitted;
      const currentValue = normalizeTiebreakerValue(tiebreakerInputs[entry.betKey]);
      if (normalizedExisting && fallbackSubmitted && normalizedExisting === fallbackSubmitted) {
        tiebreakerLastSubmittedRef.current.delete(entry.betKey);
      }
      if (baseline && currentValue === baseline) {
        const previous = tiebreakerDirtyRef.current.get(entry.betKey);
        if (previous) {
          tiebreakerDirtyRef.current.set(entry.betKey, false);
          onTiebreakerDirtyChange(entry.betKey, false);
        }
      }
    });

    tiebreakerDirtyRef.current.forEach((previousDirty, betKey) => {
      if (!activeKeys.has(betKey) && previousDirty) {
        tiebreakerDirtyRef.current.set(betKey, false);
        onTiebreakerDirtyChange(betKey, false);
      }
    });
  }, [betEntries, onTiebreakerDirtyChange, tiebreakerInputs]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: animations.duration.normal,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  const isGameStarted = useMemo(() => {
    if (isGameStartedOverride !== undefined) return isGameStartedOverride;
    const start = new Date(game.gameTime);
    return start <= getCurrentTime();
  }, [game.gameTime, isGameStartedOverride]);

  const isEntryLocked = useCallback(
    (entry: GameBetEntry) => {
      if (adminUserId) return false;
      if (entry.bet.locked === true) return true;
      const cutoffValue = entry.bet.pickLockCutoffAt ?? entry.bet.cutoffAt ?? game.gameTime;
      const cutoff = new Date(cutoffValue);
      return !Number.isNaN(cutoff.getTime()) && cutoff <= getCurrentTime();
    },
    [adminUserId, game.gameTime],
  );

  const isGameLocked = useMemo(() => {
    if (betEntries.length === 0) {
      return isGameStarted;
    }
    return betEntries.every(isEntryLocked);
  }, [betEntries, isEntryLocked, isGameStarted]);

  const submitPickMutation = useMutation<unknown, Error, SubmitPayload>({
    mutationKey: ['submitPick'],
    mutationFn: async ({ teamId, betEntry, side, tiebreakerGuess }: SubmitPayload) => {
      const call = adminUserId
        ? trpcClient.picks.adminOverride.mutate({
            gameId: game.id,
            leagueId,
            userId: adminUserId,
            selectedTeamId: teamId,
            side,
            ...(seasonId ? { seasonId } : {}),
            ...(betEntry.bet.leagueWeekBetId ? { leagueWeekBetId: betEntry.bet.leagueWeekBetId } : {}),
            betType: betEntry.bet.betType,
            ...(tiebreakerGuess !== undefined ? { tiebreakerGuess } : {}),
          })
        : trpcClient.picks.submit.mutate({
            gameId: game.id,
            leagueId,
            selectedTeamId: teamId,
            side,
            seasonId: seasonId ?? undefined,
            leagueWeekBetId: betEntry.bet.leagueWeekBetId ?? undefined,
            betType: betEntry.bet.betType,
            tiebreakerGuess,
          });

      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Pick submission timed out')), 5000),
      );

      return Promise.race([call, timeout]);
    },
    onSuccess: async (_data, variables) => {
      queryClient.setQueryData<any[] | undefined>(picksQueryKey, (current) => {
        const normalized = current ? [...current] : [];
        const entryKey = variables.betEntry.betKey;
        const tiebreakerDetails =
          typeof variables.tiebreakerGuess === 'number'
            ? { tiebreakerGuess: variables.tiebreakerGuess }
            : undefined;
        const existingIndex = normalized.findIndex((item: any) => {
          const pick = item?.picks ?? item;
          return derivePickKey(pick) === entryKey;
        });

        if (existingIndex >= 0) {
          const existing = normalized[existingIndex];
          if (existing?.picks) {
            normalized[existingIndex] = {
              ...existing,
              picks: {
                ...existing.picks,
                selectedTeamId: variables.teamId,
                side: variables.side,
                leagueWeekBetId: existing.picks.leagueWeekBetId ?? variables.betEntry.bet.leagueWeekBetId,
                betType: variables.betEntry.bet.betType,
                details: tiebreakerDetails ?? existing.picks.details,
              },
            };
          } else {
            normalized[existingIndex] = {
              ...existing,
              selectedTeamId: variables.teamId,
              side: variables.side,
              leagueWeekBetId: existing.leagueWeekBetId ?? variables.betEntry.bet.leagueWeekBetId,
              betType: variables.betEntry.bet.betType,
              details: tiebreakerDetails ?? existing.details,
            };
          }
        } else {
          normalized.push({
            id: `optimistic-${entryKey}`,
            gameId: game.id,
            leagueId,
            weekId: weekId ?? undefined,
            selectedTeamId: variables.teamId,
            side: variables.side,
            leagueWeekBetId: variables.betEntry.bet.leagueWeekBetId,
            betType: variables.betEntry.bet.betType,
            details: tiebreakerDetails,
          });
        }

        return normalized;
      });

      if (typeof variables.tiebreakerGuess === 'number') {
        pendingTiebreakerRef.current.delete(variables.betEntry.betKey);
        tiebreakerLastSubmittedRef.current.set(
          variables.betEntry.betKey,
          normalizeTiebreakerValue(variables.tiebreakerGuess),
        );
      }

      toast.show({
        type: 'success',
        text1: 'Pick saved',
        text2: 'Your pick was saved successfully.',
      });
      if (!adminUserId) {
        queryClient.invalidateQueries(trpc.leagues.achievements.queryFilter());
      }
    },
    onError: (error, variables) => {
      setLocalSelections((prev) => ({
        ...prev,
        [variables.betEntry.betKey]: variables.previousSelection,
      }));
      pendingSelectionsRef.current.delete(variables.betEntry.betKey);
      if (variables.betEntry.bet.betType === 'total_tiebreaker') {
        setTiebreakerInputs((prev) => ({
          ...prev,
          [variables.betEntry.betKey]: variables.previousSelection ?? '',
        }));
        pendingTiebreakerRef.current.delete(variables.betEntry.betKey);
      }

      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Please login again.',
        });
        return;
      }

      if (error.message === PICK_LOCKED_ERROR) {
        toast.show({
          type: 'error',
          text1: 'Picks are locked',
          text2: 'The game has started, so your existing pick was not changed.',
        });
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to submit pick.';
      toast.show({
        type: 'error',
        text1: 'Pick not saved',
        text2: message,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: picksQueryKey });
      queryClient.invalidateQueries(trpc.picks.list.queryFilter());
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId, 'picks'] });
    },
  });

  const handleSelection = (entry: GameBetEntry, teamId: string, side?: string) => {
    if (isEntryLocked(entry)) return;
    const previousSelection = localSelections[entry.betKey];
    setLocalSelections((prev) => ({ ...prev, [entry.betKey]: teamId }));
    pendingSelectionsRef.current.set(entry.betKey, teamId);
    submitPickMutation.mutate({ betEntry: entry, teamId, side, previousSelection });
  };

  const handleTiebreakerSubmit = (entry: GameBetEntry, value: string) => {
    if (isEntryLocked(entry)) return;
    const trimmed = value.trim();
    if (!trimmed) {
      toast.show({
        type: 'error',
        text1: 'Enter a number',
        text2: 'Tiebreaker picks must be a valid number.',
      });
      return;
    }
    const guess = Number.parseFloat(trimmed);
    if (!Number.isFinite(guess)) {
      toast.show({
        type: 'error',
        text1: 'Invalid number',
        text2: 'Tiebreaker picks must be a valid number.',
      });
      return;
    }
    Keyboard.dismiss();
    const previousSelection = tiebreakerInputs[entry.betKey] ?? '';
    setTiebreakerInputs((prev) => ({ ...prev, [entry.betKey]: trimmed }));
    pendingTiebreakerRef.current.set(entry.betKey, trimmed);
    submitPickMutation.mutate({
      betEntry: entry,
      teamId: game.homeTeam.id,
      side: 'one',
      tiebreakerGuess: guess,
      previousSelection,
    });
  };

  const getPickOutcome = useCallback(
    (entry: GameBetEntry, selectedTeamId?: string): PickOutcome | null => {
      if (entry.bet.betType === 'total_tiebreaker') return null;
      if (!selectedTeamId) return null;
      
      const isMultiOption =
        entry.bet.betType === 'custom_prop' &&
        entry.bet.displayInfo?.options &&
        Array.isArray(entry.bet.displayInfo.options) &&
        entry.bet.displayInfo.options.length > 0;

      const side = isMultiOption
        ? (selectedTeamId as BetSide)
        : inferBetSideFromSelection(game, selectedTeamId);

      if (!side) return null;
      const winningSide = resolveWinningSide(entry.bet, {
        homeTeam: game.homeTeam,
        awayTeam: game.awayTeam,
        homeScore: typeof game.homeScore === 'number' ? game.homeScore : null,
        awayScore: typeof game.awayScore === 'number' ? game.awayScore : null,
        winnerId: game.winnerId,
      }) as BetSide | 'push' | null;
      if (!winningSide) return null;
      if (entry.bet.betType !== 'custom_prop' && !game.isCompleted) return null;
      if (winningSide === 'push') return 'push';
      return winningSide === side ? 'win' : 'loss';
    },
    [game],
  );

  const selectedTeamPickState = useMemo<GameScoreboardPickState | null>(() => {
    const selectedTeamEntry = betEntries.find((entry) => (
      (entry.bet.betType === 'moneyline' || entry.bet.betType === 'spread')
      && Boolean(localSelections[entry.betKey])
    ));
    if (!selectedTeamEntry) return null;
    if (!game.isCompleted) return 'in-progress';

    const outcome = getPickOutcome(selectedTeamEntry, localSelections[selectedTeamEntry.betKey]);
    if (outcome === 'win') return 'correct';
    if (outcome === 'loss') return 'incorrect';
    return null;
  }, [betEntries, game.isCompleted, getPickOutcome, localSelections]);

  const selectedSpreadPick = useMemo(() => {
    const entry = betEntries.find((candidate) => (
      candidate.bet.betType === 'spread' && Boolean(localSelections[candidate.betKey])
    ));
    if (!entry) return null;
    const homeSpread = parseDisplayNumber(entry.bet.displayInfo?.spread);
    if (homeSpread === null) return null;
    const selectedTeamId = localSelections[entry.betKey];
    if (selectedTeamId === game.homeTeam.id) {
      return { teamLabel: game.homeTeam.abbreviation, line: homeSpread, side: 'home' as const };
    }
    if (selectedTeamId === game.awayTeam.id) {
      return { teamLabel: game.awayTeam.abbreviation, line: -homeSpread, side: 'away' as const };
    }
    return null;
  }, [betEntries, game.awayTeam.abbreviation, game.awayTeam.id, game.homeTeam.abbreviation, game.homeTeam.id, localSelections]);

  const renderTiebreakerInput = (entry: GameBetEntry) => {
    const { bet } = entry;
    const value = tiebreakerInputs[entry.betKey] ?? '';
    const guess = Number.parseFloat(value);
    const isValidGuess = Number.isFinite(guess);
    const resolvedTotal =
      typeof bet.displayInfo?.resolvedTotal === 'number' ? bet.displayInfo.resolvedTotal : null;
    const showResult = game.isCompleted && resolvedTotal !== null && isValidGuess;
    const delta = showResult ? Math.abs(resolvedTotal - guess) : null;
    const locked = isEntryLocked(entry);
    const canSubmit = !locked && isValidGuess;
    const inputAccessoryViewID = `tiebreaker-${game.id}-${entry.betKey}`;

    return (
      <Box key={entry.betKey} marginBottom="2">
        <Box marginBottom="1" flexDirection="row" alignItems="center">
          <Text
            fontSize={12}
            fontWeight="bold"
            textTransform="uppercase"
            style={platformLetterSpacingStyle}
            color="textSecondary"
          >
            {buildBetLabel(bet.betType, bet.displayInfo)}
          </Text>
          <Box marginLeft="2">
            <BetHelpButton betType={bet.betType} />
          </Box>
        </Box>
        <Text marginBottom="2" variant="bodySmall" fontWeight="500" color="mutedForeground">
          Closest total points wins the tiebreaker.
        </Text>
        <Box flexDirection="row" alignItems="center" gap="s">
          <Box flex={1}>
            <Input
              value={value}
              onChangeText={(text) => {
                setTiebreakerInputs((prev) => ({ ...prev, [entry.betKey]: text }));
                if (onTiebreakerDirtyChange) {
                  const existingGuess = entry.pick?.details?.tiebreakerGuess;
                  const normalizedExisting = normalizeTiebreakerValue(existingGuess);
                  const normalizedText = normalizeTiebreakerValue(text);
                  const isDirty = normalizedText !== '' && normalizedText !== normalizedExisting;
                  const previous = tiebreakerDirtyRef.current.get(entry.betKey);
                  if (previous !== isDirty) {
                    tiebreakerDirtyRef.current.set(entry.betKey, isDirty);
                    onTiebreakerDirtyChange(entry.betKey, isDirty);
                  }
                }
              }}
              keyboardType="decimal-pad"
              returnKeyType="done"
              enterKeyHint="done"
              submitBehavior="blurAndSubmit"
              onSubmitEditing={() => handleTiebreakerSubmit(entry, value)}
              inputAccessoryViewID={Platform.OS === 'ios' ? inputAccessoryViewID : undefined}
              placeholder="Enter total points"
              editable={!locked}
            />
          </Box>
          <PressableBox
            onPress={() => handleTiebreakerSubmit(entry, value)}
            disabled={!canSubmit}
            paddingHorizontal="m"
            paddingVertical="s"
            borderRadius="none"
            borderWidth={3}
            borderColor="press"
            backgroundColor={canSubmit ? tokens.colors.ink.acid : tokens.semanticColors.light.muted}
          >
            <Text
              variant="button"
              color={canSubmit ? tokens.colors.ink.press : tokens.colors.text.secondary}
            >
              Save
            </Text>
          </PressableBox>
        </Box>
        {Platform.OS === 'ios' && (
          <InputAccessoryView nativeID={inputAccessoryViewID}>
            <Box
              alignItems="flex-end"
              backgroundColor="background"
              borderTopWidth={3}
              borderColor="press"
              paddingHorizontal="m"
              paddingVertical="s"
            >
              <PressableBox
                accessibilityRole="button"
                accessibilityLabel="Done entering total points"
                onPress={() => {
                  if (canSubmit) {
                    handleTiebreakerSubmit(entry, value);
                  } else {
                    Keyboard.dismiss();
                  }
                }}
                borderRadius="none"
                borderWidth={3}
                borderColor="press"
                paddingHorizontal="m"
                paddingVertical="s"
                backgroundColor={canSubmit ? tokens.colors.ink.acid : tokens.semanticColors.light.muted}
              >
                <Text
                  variant="button"
                  color={canSubmit ? tokens.colors.ink.press : tokens.colors.text.secondary}
                >
                  Done
                </Text>
              </PressableBox>
            </Box>
          </InputAccessoryView>
        )}
        {showResult && (
          <Text marginTop="2" variant="bodySmall" color="mutedForeground">
            Resolved total: {resolvedTotal} (Δ {delta})
          </Text>
        )}
        {game.isCompleted && resolvedTotal === null && (
          <Text marginTop="2" variant="bodySmall" color="mutedForeground">
            Awaiting final total.
          </Text>
        )}
      </Box>
    );
  };

  const renderBetButtons = (
    entry: GameBetEntry,
    scoringSystem: LegacyScoringSystem,
    gameOdds: { homeOdds: number | string | null; awayOdds: number | string | null },
    orientation: 'horizontal' | 'vertical' = 'horizontal',
  ) => {
    const { bet } = entry;
    if (bet.betType === 'total_tiebreaker') {
      return renderTiebreakerInput(entry);
    }
    const spreadValue = parseDisplayNumber(bet.displayInfo?.spread);
    const totalValue = typeof bet.displayInfo?.total === 'number' ? bet.displayInfo.total : null;
    const sideLabelOne =
      typeof bet.displayInfo?.sideLabelOne === 'string' ? bet.displayInfo.sideLabelOne : null;
    const sideLabelTwo =
      typeof bet.displayInfo?.sideLabelTwo === 'string' ? bet.displayInfo.sideLabelTwo : null;
    const description =
      bet.betType === 'custom_prop' && typeof bet.displayInfo?.description === 'string'
        ? bet.displayInfo.description
        : null;
    const selection = localSelections[entry.betKey];
    const outcome = getPickOutcome(entry, selection);

    // Helper to normalize options
    const getOptions = (): BetOption[] => {
      if (bet.betType === 'total') {
        return [
          {
            key: 'one',
            label: 'Over',
            value: formatTotal(totalValue),
            teamId: game.homeTeam.id,
            odds: bet.oddsOne,
            side: 'one' as BetSide,
          },
          {
            key: 'two',
            label: 'Under',
            value: formatTotal(totalValue),
            teamId: game.awayTeam.id,
            odds: bet.oddsTwo,
            side: 'two' as BetSide,
          },
        ];
      }
      
      if (bet.betType === 'custom_prop') {
        if (bet.displayInfo?.options && Array.isArray(bet.displayInfo.options) && bet.displayInfo.options.length > 0) {
          return bet.displayInfo.options.map((opt, idx) => ({
            key: opt.side || `opt-${idx}`,
            label: opt.label,
            value: formatOdds(opt.odds),
            teamId: opt.side, 
            odds: opt.odds,
            side: opt.side as BetSide,
            playerId: opt.playerId,
            position: opt.position,
            teamAbbr: opt.teamAbbr,
            theme: opt.theme,
          }));
        }
        
        return [
          {
            key: 'one',
            label: sideLabelOne ?? 'Side 1',
            value: formatOdds(bet.oddsOne),
            teamId: game.homeTeam.id,
            odds: bet.oddsOne,
            side: 'one' as BetSide,
          },
          {
            key: 'two',
            label: sideLabelTwo ?? 'Side 2',
            value: formatOdds(bet.oddsTwo),
            teamId: game.awayTeam.id,
            odds: bet.oddsTwo,
            side: 'two' as BetSide,
          },
        ];
      }

      return [
        {
          key: 'two',
          label: '',
          value:
            bet.betType === 'spread'
              ? formatSpread(spreadValue !== null ? -spreadValue : null)
              : formatOdds(bet.oddsTwo ?? (game.awayOdds ? Number(game.awayOdds) : null)),
          teamId: game.awayTeam.id,
          odds: bet.oddsTwo ?? (game.awayOdds ? Number(game.awayOdds) : null),
          side: 'two' as BetSide,
        },
        {
          key: 'one',
          label: '',
          value:
            bet.betType === 'spread'
              ? formatSpread(spreadValue)
              : formatOdds(bet.oddsOne ?? (game.homeOdds ? Number(game.homeOdds) : null)),
          teamId: game.homeTeam.id,
          odds: bet.oddsOne ?? (game.homeOdds ? Number(game.homeOdds) : null),
          side: 'one' as BetSide,
        },
      ];
    };

    const options = getOptions();
    const isTeamPickBet = bet.betType === 'moneyline' || bet.betType === 'spread';

    const isMultiRow = options.length > 2;
    const isVertical = orientation === 'vertical';
    const isStacked = isVertical && !isMultiRow;
    const usesFeaturedOptionLayout = isFeatured && bet.betType === 'custom_prop';
    const showAtBadge = isTeamPickBet && !isStacked && !isMultiRow && options.length === 2;
    const showGameScoreboard = isTeamPickBet && isGameStarted;
    const atBadgeSize = isCompactLayout ? 26 : 30;
    const atBadgeTop = Math.round(teamPickLogoSize * 0.8);
    const atGlyphFontSize = isCompactLayout ? 18 : 22;
    const atBadgeStyle = {
      height: atBadgeSize,
      top: atBadgeTop - 2,
      transform: [{ translateX: -atBadgeSize / 2 }],
      width: atBadgeSize,
    };
    const atGlyphStyle = {
      fontSize: atGlyphFontSize,
      lineHeight: atBadgeSize - 4,
      textAlign: 'center' as const,
      textTransform: 'none' as const,
      width: atBadgeSize - 4,
    };
    const optionsContainerStyle = {
      backgroundColor: isMultiRow && !usesFeaturedOptionLayout ? tokens.colors.ink.paperDark : 'transparent',
      padding: isMultiRow && !usesFeaturedOptionLayout ? 6 : 0,
    };
    const optionRowStyles = [
      isMultiRow && !usesFeaturedOptionLayout ? { marginHorizontal: -4 } : undefined,
      showAtBadge ? { position: 'relative' as const } : undefined,
    ];

    return (
      <Box key={entry.betKey} marginBottom="2">
        {bet.betType !== 'moneyline' && bet.betType !== 'spread' && (!isFeatured || !featuredPropTitle) && (
          <Box marginBottom="1" flexDirection="row" alignItems="center">
            <Text 
              fontSize={12} 
              fontWeight="bold" 
              textTransform="uppercase" 
              style={[
                platformLetterSpacingStyle,
                isFeatured ? styles.featuredPropLabel : undefined,
              ]}
              color="textSecondary"
            >
              {buildBetLabel(bet.betType, bet.displayInfo)}
            </Text>
            {bet.betType !== 'custom_prop' && (
              <Box marginLeft="2">
                <BetHelpButton betType={bet.betType} />
              </Box>
            )}
          </Box>
        )}
        {Boolean(description) && (!isFeatured || !featuredPropTitle) && bet.betType !== 'moneyline' && bet.betType !== 'spread' && (
          <Text
            marginBottom="2"
            variant="bodySmall"
            fontWeight="500"
            color="mutedForeground"
            style={isFeatured ? styles.featuredPropDescription : undefined}
          >
            {description}
          </Text>
        )}
        <Box 
          borderRadius="none"
          borderWidth={isMultiRow && !usesFeaturedOptionLayout ? 3 : 0}
          borderColor="press"
          style={optionsContainerStyle}
        >
          <Box
            flexDirection={usesFeaturedOptionLayout || isStacked ? "column" : "row"}
            flexWrap={usesFeaturedOptionLayout ? "nowrap" : isMultiRow ? "wrap" : "nowrap"}
            style={optionRowStyles}
            accessibilityRole={isTeamPickBet ? 'radiogroup' : undefined}
            accessibilityLabel={isTeamPickBet ? `${bet.betType} pick` : undefined}
          >
            {options.map((option, optionIndex) => {
              const isSelected = localSelections[entry.betKey] === option.teamId;
              const hasSelectionForEntry = !!localSelections[entry.betKey];
              const isCompletedUnselected = game.isCompleted && !isSelected;
              const isUnselectedTeamMuted = isTeamPickBet && hasSelectionForEntry && !isSelected;
              const isUnselectedFeaturedOption = usesFeaturedOptionLayout && hasSelectionForEntry && !isSelected;
              const pointsValue = getBetPoints({
                scoringSystem,
                bet,
                side: option.side,
                gameOdds,
              });
              const pointsLabel = formatPointsLabel(pointsValue, scoringSystem);
              const { value: pointsValueLabel, unit: pointsUnitLabel } = splitPointsLabel(pointsLabel);
              const isSinglePointGameBet =
                isTeamPickBet && scoringSystem === 'totalWins' && !bet.scoringConfigOverride && pointsValue === 1;
              const isSelectedUnresolved = isSelected && !outcome;
              const isSelectedWin = isSelected && outcome === 'win';
              const isSelectedLoss = isSelected && outcome === 'loss';

              // Determine visual states
              let borderColor = 'border';
              let backgroundColor = 'surface';
              
              if (isSelected) {
                if (isSelectedWin) {
                   borderColor = 'success';
                   backgroundColor = tokens.colors.state.successBg; // success-50
                } else if (isSelectedLoss) {
                   borderColor = 'destructive'; // error-500
                   backgroundColor = tokens.colors.state.errorBg; // error-50
                } else {
                   borderColor = 'press';
                   backgroundColor = tokens.colors.ink.press;
                }
              } else if (isCompletedUnselected) {
                 borderColor = 'border'; // neutral-200
                 backgroundColor = tokens.semanticColors.light.muted; // neutral-50
              }

              // Text Color Resolution
              const resolveTextColor = (defaultColor: keyof typeof colors.text) => {
                if (isSelectedLoss) return tokens.colors.error[700];
                if (isSelectedWin) return tokens.colors.success[700];
                if (isSelectedUnresolved) return tokens.colors.text.inverse;
                if (isCompletedUnselected) return tokens.colors.neutral[400];
                return colors.text[defaultColor]; // Using colors object directly for specific shades if needed, or mapped theme keys
              };
              
              const defaultTextColor = resolveTextColor('primary'); // text-foreground

              const team =
                option.teamId === game.awayTeam.id
                  ? game.awayTeam
                  : option.teamId === game.homeTeam.id
                    ? game.homeTeam
                    : null;
              const teamBranding = team ? getTeamBranding(team) : null;
              const isWorldCupTeamOption = isTeamPickBet && Boolean(teamBranding?.isWorldCupTeam);
              const teamPrimaryColor = isTeamPickBet && team
                ? teamBranding?.primaryColor ?? team.primaryColor ?? getTeamPrimaryColor(team.abbreviation)
                : null;
              const teamSecondaryColor = isTeamPickBet && team
                ? teamBranding?.secondaryColor ?? team.secondaryColor ?? null
                : null;
              const teamRecord = team?.record ?? (
                team?.id === game.awayTeam.id
                  ? matchupAnalysis?.awayTeam?.record?.overall
                  : team?.id === game.homeTeam.id
                    ? matchupAnalysis?.homeTeam?.record?.overall
                    : null
              ) ?? null;
              const isTeamSelectedResolved = isTeamPickBet && (isSelectedWin || isSelectedLoss) && !!teamPrimaryColor;
              const isTeamSelectedUnresolved = isTeamPickBet && isSelectedUnresolved && !!teamPrimaryColor;
              const showSelectedTeamBackdrop =
                isTeamPickBet &&
                isSelected &&
                !isWorldCupTeamOption &&
                !!teamPrimaryColor;
              const showUnselectedTeamBackdrop =
                isTeamPickBet && !isWorldCupTeamOption && !isSelected && !!teamPrimaryColor;
              const showTeamColorBackdrop = showSelectedTeamBackdrop || showUnselectedTeamBackdrop;
              
              // Dynamic styles based on team color (used in PressableBox content)

              const teamTextColor = showTeamColorBackdrop && teamPrimaryColor
                ? getContrastingTextColor(
                  teamPrimaryColor,
                  tokens.colors.text.inverse,
                  defaultTextColor,
                )
                : isUnselectedTeamMuted
                  ? tokens.colors.neutral[500]
                  : isTeamSelectedUnresolved || isTeamSelectedResolved || (isWorldCupTeamOption && !isCompletedUnselected)
                    ? tokens.colors.text.inverse
                    : defaultTextColor;

              const teamLineLength = team?.name?.length ?? option.label.length;
              const baseTeamFontSize = isCompactLayout ? 18 : 24;
              const minTeamFontSize = isCompactLayout ? 11 : 14;
              let teamFontSize = baseTeamFontSize;
              if (teamLineLength > 9) teamFontSize -= 2;
              if (teamLineLength > 12) teamFontSize -= 3;
              if (teamLineLength > 16) teamFontSize -= 4;
              if (teamLineLength > 20) teamFontSize -= 5;
              teamFontSize = Math.max(minTeamFontSize, teamFontSize);

              // Length sets the size. Auto-fit is omitted: on iOS it ignores
              // minimumFontScale and can drop short nicknames to 4pt.
              const teamNameTextStyle = {
                fontSize: teamFontSize,
                lineHeight: Math.round(teamFontSize * 1.15),
                letterSpacing: isCompactLayout ? 0.6 : 0.8,
                fontFamily: 'Oswald_600SemiBold',
                textTransform: 'uppercase' as const,
                color: teamTextColor,
                width: '100%' as const,
              };

              const isLongTeamName = teamLineLength > 12;
              const teamLogoSizeOverride = isLongTeamName
                ? (isCompactLayout ? 36 : 48)
                : (isCompactLayout ? 44 : teamPickLogoSize);

              const teamPickTextOutlineStyle = showSelectedTeamBackdrop && !isUnselectedTeamMuted
                ? styles.selectedTeamTextOutline
                : (isWorldCupTeamOption && !isUnselectedTeamMuted ? styles.worldCupTextShadow : undefined);
              const teamResultStyle = isSelectedWin
                ? styles.correctTeamResult
                : isSelectedLoss
                  ? styles.incorrectTeamResult
                  : undefined;
              const teamCardBorderColor = isSelectedWin
                ? 'success'
                : isSelectedLoss
                  ? 'destructive'
                  : 'press';
              const teamOptionKey = `${entry.betKey}:${option.key}`;
              const isTeamOptionPressed = pressedTeamOptionKey === teamOptionKey;
              const teamCardPadding = isTeamPickBet
                ? 8 + (showAtBadge ? Math.ceil(atBadgeSize / 2) + 4 : 0)
                : 6;
              const teamOptionPressStyle = isTeamOptionPressed ? {
                transform: [
                  { translateX: teamOptionPressAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 6] }) },
                  { translateY: teamOptionPressAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 8] }) },
                ],
              } : undefined;
              const optionPaddingStyle = {
                paddingHorizontal: teamCardPadding,
                paddingTop: isTeamPickBet && isCompactLayout ? 44 : isTeamPickBet ? 12 : 8,
                paddingBottom: isTeamPickBet ? 12 : 8,
                minHeight: isTeamPickBet && isCompactLayout ? 100 : undefined,
              };
              const optionBackgroundStyle = {
                backgroundColor:
                  isWorldCupTeamOption || showTeamColorBackdrop
                    ? 'rgba(255, 255, 255, 0.08)'
                    : backgroundColor,
              };
              const selectedTeamStyle = isTeamSelectedUnresolved && !isWorldCupTeamOption && !showTeamColorBackdrop
                ? { borderColor: teamPrimaryColor, backgroundColor: teamPrimaryColor }
                : undefined;
              const mutedTeamContentStyle = { opacity: isUnselectedTeamMuted ? 0.85 : 1 };
              const singlePointContainerStyle = { marginTop: 8 };
              const teamValueStyle = { color: teamTextColor, lineHeight: 22 };
              const teamPointsStyle = { color: teamTextColor, lineHeight: 20 };
              const pointsStrikeStyle = isSelectedLoss ? { textDecorationLine: 'line-through' as const } : undefined;
              const teamProbabilityStyle = { marginTop: 4 };
              const propValueStyle = {
                letterSpacing: Platform.OS === 'ios' ? 0 : 0.25,
                color: isCompletedUnselected ? tokens.colors.neutral[400] : resolveTextColor('secondary'),
              };
              const propLabelStyle = { lineHeight: 18, color: resolveTextColor('primary') };
              const totalLabelStyle = { letterSpacing: 0.5, color: resolveTextColor('primary') };
              const optionLabelStyle = { letterSpacing: Platform.OS === 'ios' ? 0 : 0.25, color: tokens.colors.neutral[400] };
              const optionValueStyle = { lineHeight: 24, color: resolveTextColor('primary') };
              const pointsValueStyle = { lineHeight: 22, color: resolveTextColor('primary') };
              const optionScoreStyle = { marginTop: 2, color: tokens.colors.neutral[400] };
              const teamRecordStyle = {
                color: teamTextColor,
                letterSpacing: 0.4,
                marginTop: 2,
                opacity: isUnselectedTeamMuted ? 0.75 : 0.9,
              };
              const selectedTeamHighlightColor = isSelectedWin
                ? tokens.colors.state.success
                : isSelectedLoss
                  ? tokens.colors.state.error
                  : teamSecondaryColor;
              const selectedTeamHighlightStyle = selectedTeamHighlightColor
                ? { backgroundColor: selectedTeamHighlightColor }
                : undefined;
              const isFeaturedPropOption = usesFeaturedOptionLayout;
              const featuredTeamAbbr = option.teamAbbr?.trim().toUpperCase() || null;
              const featuredTeamBranding = featuredTeamAbbr
                ? getTeamBranding({ abbreviation: featuredTeamAbbr })
                : null;
              const featuredTeamColor = featuredTeamBranding?.primaryColor ?? null;
              const featuredTheme = option.theme;
              const hasCustomFeaturedBackdrop = Boolean(featuredTheme?.backgroundColor || featuredTheme?.backgroundImagePath);
              const featuredThemeForegroundColor = isUnselectedFeaturedOption
                ? tokens.colors.neutral[800]
                : featuredTheme?.textColor ?? (
                  featuredTheme?.backgroundColor
                    ? getContrastingTextColor(
                      featuredTheme.backgroundColor,
                      tokens.colors.text.inverse,
                      tokens.colors.text.primary,
                    )
                    : featuredTheme?.backgroundImagePath
                      ? tokens.colors.text.inverse
                      : undefined
              );
              const featuredThemeTextStyle = featuredThemeForegroundColor
                ? { color: featuredThemeForegroundColor }
                : undefined;
              const featuredThemeBackgroundStyle = featuredTheme?.backgroundColor
                ? { backgroundColor: featuredTheme.backgroundColor }
                : undefined;
              const featuredThemeAccentStyle = !isUnselectedFeaturedOption && featuredTheme?.accentColor
                ? { borderColor: featuredTheme.accentColor }
                : undefined;
              const featuredThemeTintStyle = featuredTheme?.backgroundColor
                ? { backgroundColor: featuredTheme.backgroundColor }
                : undefined;
              const featuredThemePointRailStyle = !isUnselectedFeaturedOption && featuredTheme?.accentColor
                ? { borderLeftColor: featuredTheme.accentColor }
                : undefined;
              const featuredArtUri = featuredTheme?.backgroundImagePath
                ? resolveAvatarUri(featuredTheme.backgroundImagePath)
                : null;
              const featuredMetadata = [featuredTeamAbbr, option.position?.trim()].filter(Boolean).join(', ');
              const featuredOptionLabel = featuredTeamAbbr
                ? option.label.replace(/\s*\([^)]*\)\s*$/, '').trim()
                : option.label;
              const featuredOptionNameSize = isCompactLayout ? styles.featuredOptionNameCompact : undefined;

              if (isFeaturedPropOption) {
                return (
                  <Box key={option.key} marginBottom={optionIndex < options.length - 1 ? '3' : '0'}>
                    <Animated.View
                      style={[
                        isSelected ? styles.featuredOptionSelectedShadow : styles.featuredOptionShadow,
                        teamOptionPressStyle,
                      ]}
                    >
                      <PressableBox
                        accessibilityRole="button"
                        accessibilityState={{ disabled: isEntryLocked(entry) || submitPickMutation.isPending, selected: isSelected }}
                        accessibilityLabel={`Pick ${featuredOptionLabel}, ${option.value}, worth ${pointsLabel}`}
                        onPress={() => handleSelection(entry, option.teamId, option.side)}
                        onPressIn={() => handleTeamOptionPressIn(teamOptionKey)}
                        onPressOut={() => handleTeamOptionPressOut(teamOptionKey)}
                        disabled={isEntryLocked(entry) || submitPickMutation.isPending}
                        style={[
                          styles.featuredOptionSurface,
                          isSelected && styles.featuredOptionSurfaceSelected,
                          featuredThemeBackgroundStyle,
                          featuredThemeAccentStyle,
                        ]}
                      >
                        {featuredArtUri ? (
                          <ImageBackground
                            source={{ uri: featuredArtUri }}
                            resizeMode="cover"
                            style={styles.featuredOptionArtBackground}
                          >
                            <Box style={styles.featuredOptionArtScrim} />
                            {featuredThemeTintStyle ? (
                              <Box style={[styles.featuredOptionArtTint, featuredThemeTintStyle]} />
                            ) : null}
                          </ImageBackground>
                        ) : null}
                        {isUnselectedFeaturedOption ? (
                          <Box pointerEvents="none" style={styles.featuredOptionUnselectedWash} />
                        ) : null}
                        {!hasCustomFeaturedBackdrop && featuredTeamColor ? (
                          <SelectedTeamBackdrop
                            logoUrl={featuredTeamBranding?.logoUrl}
                            logoTintColor={featuredTeamBranding?.backdropLogoTintColor}
                            primaryColor={featuredTeamColor}
                            tone={isSelected ? 'solid' : 'tinted'}
                          />
                        ) : null}
                        <Box
                          style={[
                            styles.featuredOptionContent,
                            isUnselectedFeaturedOption && styles.featuredOptionContentUnselected,
                          ]}
                        >
                          <Box style={styles.featuredOptionInfo}>
                            <Text
                              style={[
                                styles.featuredOptionName,
                                featuredOptionNameSize,
                                isSelected && styles.featuredOptionNameSelected,
                                featuredThemeTextStyle,
                              ]}
                              numberOfLines={2}
                              adjustsFontSizeToFit
                              minimumFontScale={0.65}
                            >
                              {featuredOptionLabel}
                            </Text>
                            {featuredMetadata ? (
                              <Text style={[styles.featuredOptionMeta, isSelected && styles.featuredOptionMetaSelected, featuredThemeTextStyle]}>
                                {featuredMetadata}
                              </Text>
                            ) : null}
                            <Text style={[styles.featuredOptionOdds, isSelected && styles.featuredOptionOddsSelected, featuredThemeTextStyle]}>
                              {option.value}
                            </Text>
                          </Box>
                          <Box style={[styles.featuredOptionPoints, isSelected && styles.featuredOptionPointsSelected, featuredThemePointRailStyle]}>
                            <Text style={[styles.featuredOptionPointsValue, isSelected && styles.featuredOptionPointsValueSelected, featuredThemeTextStyle]}>
                              {pointsValueLabel}
                              {pointsUnitLabel ? (
                                <Text style={[styles.featuredOptionPointsUnit, isSelected && styles.featuredOptionPointsUnitSelected, featuredThemeTextStyle]}>
                                  {' '}{pointsUnitLabel}
                                </Text>
                              ) : null}
                            </Text>
                            <Text style={[styles.featuredOptionProbability, isSelected && styles.featuredOptionProbabilitySelected, featuredThemeTextStyle]}>
                              {`${formatImpliedProbability(option.odds)} implied win prob.`}
                            </Text>
                          </Box>
                        </Box>
                      </PressableBox>
                    </Animated.View>
                  </Box>
                );
              }

              return (
                <Box
                  key={option.key}
                  width={isStacked ? "100%" : isMultiRow ? "50%" : undefined}
                  flex={(!isStacked && !isMultiRow) ? 1 : undefined}
                  padding={showAtBadge ? "0" : "1"}
                  marginBottom={(isStacked && optionIndex < options.length - 1) ? "2" : "0"} // Use "0"
                  marginRight={(!isStacked && !isMultiRow && optionIndex < options.length - 1) ? (showAtBadge ? "0" : "2") : "0"}
                >
                  <PressableBox
                    accessibilityRole={isTeamPickBet ? 'radio' : undefined}
                    onPress={() => handleSelection(entry, option.teamId, option.side)}
                    onPressIn={() => handleTeamOptionPressIn(teamOptionKey)}
                    onPressOut={() => handleTeamOptionPressOut(teamOptionKey)}
                    disabled={isEntryLocked(entry) || submitPickMutation.isPending}
                    accessibilityState={{ checked: isSelected }}
                    aria-checked={Platform.OS === 'web' ? isSelected : undefined}
                    style={showAtBadge ? styles.teamPickOptionPressable : undefined}
                  >
                    <Box
                      width="100%"
                      justifyContent="center"
                      borderRadius="none"
                      borderWidth={isTeamPickBet ? 2 : 0}
                      style={[
                        optionPaddingStyle,
                        showAtBadge ? styles.teamPickOptionSurface : undefined,
                        optionBackgroundStyle,
                        selectedTeamStyle,
                        teamResultStyle,
                      ]}
                      borderColor={(isTeamPickBet ? teamCardBorderColor : borderColor) as any}
                      overflow={isTeamPickBet ? 'hidden' : undefined}
                      position="relative"
                    >
                      {isTeamPickBet && isSelected ? (
                        <>
                          <Box
                            pointerEvents="none"
                            style={[
                              styles.selectedTeamHighlight,
                              styles.selectedTeamHighlightHorizontal,
                              styles.selectedTeamHighlightTop,
                              selectedTeamHighlightStyle,
                            ]}
                          />
                          <Box
                            pointerEvents="none"
                            style={[
                              styles.selectedTeamHighlight,
                              styles.selectedTeamHighlightHorizontal,
                              styles.selectedTeamHighlightBottom,
                              selectedTeamHighlightStyle,
                            ]}
                          />
                          {isSelectedWin || isSelectedLoss ? (
                            <>
                              <Box
                                pointerEvents="none"
                                style={[
                                  styles.selectedTeamHighlight,
                                  styles.selectedTeamHighlightVertical,
                                  styles.selectedTeamHighlightLeft,
                                  selectedTeamHighlightStyle,
                                ]}
                              />
                              <Box
                                pointerEvents="none"
                                style={[
                                  styles.selectedTeamHighlight,
                                  styles.selectedTeamHighlightVertical,
                                  styles.selectedTeamHighlightRight,
                                  selectedTeamHighlightStyle,
                                ]}
                              />
                            </>
                          ) : null}
                        </>
                      ) : null}
                      {isSelectedWin || isSelectedLoss ? (
                        <Box
                          pointerEvents="none"
                          style={[
                            styles.selectedTeamResultBadge,
                            isSelectedWin ? styles.selectedTeamResultBadgeCorrect : styles.selectedTeamResultBadgeIncorrect,
                          ]}
                        >
                          {isSelectedWin ? (
                            <Check size={16} color={tokens.colors.ink.paper} />
                          ) : (
                            <X size={16} color={tokens.colors.ink.paper} />
                          )}
                        </Box>
                      ) : null}
                      {showTeamColorBackdrop && teamPrimaryColor ? (
                          <SelectedTeamBackdrop
                            logoUrl={teamBranding?.logoUrl}
                            logoTintColor={teamBranding?.backdropLogoTintColor}
                            primaryColor={teamPrimaryColor}
                            muted={showUnselectedTeamBackdrop}
                          />
                      ) : null}
                      {isWorldCupTeamOption && teamBranding ? (
                        <WorldCupFlagBackdrop
                          flagImageUrl={teamBranding.flagImageUrl}
                          primaryColor={teamBranding.primaryColor ?? tokens.colors.neutral[700]}
                        />
                      ) : null}
                      {isWorldCupTeamOption && isUnselectedTeamMuted ? (
                        <Box pointerEvents="none" style={styles.worldCupFlagMutedTint} />
                      ) : null}
                      {isTeamPickBet ? (
                        <Box width="100%" position="relative" zIndex={1} style={mutedTeamContentStyle}>
                          <Box
                            width="100%"
                            flexDirection={isCompactLayout ? 'column' : 'row'}
                            alignItems="center"
                            justifyContent={
                              isCompactLayout
                                ? 'center'
                                : team?.id === game.awayTeam.id
                                  ? 'flex-start'
                                  : 'flex-end'
                            }
                            gap="1"
                          >
                            {team && !isWorldCupTeamOption && !showTeamColorBackdrop && (isCompactLayout || team?.id === game.homeTeam.id) ? (
                              <TeamBadge team={team} size={teamLogoSizeOverride} muted={isUnselectedTeamMuted} />
                            ) : null}
                            <Text
                              flex={isCompactLayout ? undefined : 1}
                              width="100%"
                              fontWeight="600"
                              textAlign={
                                isCompactLayout
                                  ? 'center'
                                  : team?.id === game.awayTeam.id
                                    ? 'left'
                                    : 'right'
                              }
                              numberOfLines={2}
                              style={[
                                teamPickTextOutlineStyle,
                                teamNameTextStyle,
                              ]}
                            >
                              {team ? team.name : option.label}
                            </Text>
                            {team && !isWorldCupTeamOption && !showTeamColorBackdrop && !isCompactLayout && team?.id === game.awayTeam.id ? (
                              <TeamBadge team={team} size={teamLogoSizeOverride} muted={isUnselectedTeamMuted} />
                            ) : null}
                          </Box>

                          {teamRecord ? (
                            <Text
                              variant="caption"
                              fontSize={10}
                              width="100%"
                              textAlign={
                                isCompactLayout
                                  ? 'center'
                                  : team?.id === game.awayTeam.id
                                    ? 'left'
                                    : 'right'
                              }
                              style={[teamPickTextOutlineStyle, teamRecordStyle]}
                              numberOfLines={1}
                            >
                              {teamRecord}
                            </Text>
                          ) : null}

                          {isSinglePointGameBet ? (
                            <Box style={singlePointContainerStyle} width="100%" alignItems="center">
                              <Text
                                fontSize={18}
                                fontWeight="800"
                                style={[teamPickTextOutlineStyle, teamValueStyle]}
                                numberOfLines={1}
                                adjustsFontSizeToFit
                                minimumFontScale={0.8}
                              >
                                {option.value}
                              </Text>
                            </Box>
                          ) : (
                            <Box style={singlePointContainerStyle} width="100%" flexDirection="row" alignItems="center" justifyContent="space-between">
                              <Text
                                fontSize={18}
                                fontWeight="800"
                                style={[teamPickTextOutlineStyle, teamValueStyle]}
                                numberOfLines={1}
                                adjustsFontSizeToFit
                                minimumFontScale={0.8}
                              >
                                {option.value}
                              </Text>
                              <Text
                                fontSize={16}
                                fontWeight="bold"
                                style={[teamPickTextOutlineStyle, teamPointsStyle]}
                                numberOfLines={1}
                                adjustsFontSizeToFit
                                minimumFontScale={0.8}
                              >
                                <Text
                                  style={[
                                    teamPickTextOutlineStyle, 
                                    pointsStrikeStyle,
                                    { color: teamTextColor }
                                  ]}
                                >
                                  {pointsValueLabel}
                                </Text>
                                {pointsUnitLabel ? (
                                  <Text 
                                    fontSize={12} 
                                    fontWeight="500" 
                                    style={[teamPickTextOutlineStyle, { color: teamTextColor }]}
                                  >
                                    {pointsUnitLabel}
                                  </Text>
                                ) : null}
                              </Text>
                            </Box>
                          )}
                          {bet.betType === 'moneyline' && !showGameScoreboard && (
                             <Text
                               style={[
                                 teamPickTextOutlineStyle,
                                 teamProbabilityStyle,
                                 showTeamColorBackdrop && { color: teamTextColor },
                                 isTeamSelectedUnresolved && { color: teamTextColor },
                               ]}
                               fontSize={10}
                               color={(isTeamSelectedUnresolved || isSelectedUnresolved) ? 'textInverse' : 'mutedForeground'}
                             >
                               {`${formatImpliedProbability(option.odds)} implied win prob.`}
                             </Text>
                          )}
                        </Box>
                      ) : (
                        <Box width="100%" flexDirection="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap="1">
                           <Box flex={1} justifyContent="center">
                             {bet.betType === 'custom_prop' ? (
                                <>
                                  <Text 
                                    fontSize={10} 
                                    fontWeight="bold" 
                                    style={propValueStyle}
                                  >
                                    {option.value}
                                  </Text>
                                  <Text
                                    fontSize={14}
                                    fontWeight="bold"
                                    style={propLabelStyle}
                                  >
                                    {option.label}
                                  </Text>
                                </>
                             ) : bet.betType === 'total' ? (
                                <Text
                                  fontSize={16}
                                  fontWeight="900"
                                  textTransform="uppercase"
                                  style={totalLabelStyle}
                                  numberOfLines={1}
                                  adjustsFontSizeToFit
                                  minimumFontScale={0.8}
                                >
                                  {`${option.label}${option.label === 'Over' ? '\u00A0\u00A0\u00A0' : ''}\u00A0\u00A0${option.value}`}
                                </Text>
                             ) : (
                               <>
                                {option.label ? (
                                  <Text
                                    fontSize={12}
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    style={optionLabelStyle}
                                  >
                                    {option.label}
                                  </Text>
                                ) : null}
                                <Text
                                  fontSize={20}
                                  fontWeight="800"
                                  style={optionValueStyle}
                                  numberOfLines={1}
                                  adjustsFontSizeToFit
                                  minimumFontScale={0.8}
                                >
                                  {option.value}
                                </Text>
                               </>
                             )}
                           </Box>
                           <Box alignItems="flex-end" justifyContent="center">
                              <Text
                                fontSize={18}
                                fontWeight="bold"
                                style={pointsValueStyle}
                                numberOfLines={1}
                                adjustsFontSizeToFit
                                minimumFontScale={0.8}
                              >
                                <Text style={pointsStrikeStyle}>
                                  {pointsValueLabel}
                                </Text>
                                {pointsUnitLabel ? (
                                  <Text fontSize={12} fontWeight="500">{pointsUnitLabel}</Text>
                                ) : null}
                              </Text>
                           </Box>
                        </Box>
                      )}

                      {/* Implied Prob for Non-Team Props */}
                      {!isTeamPickBet && (bet.betType === 'moneyline' || bet.betType === 'custom_prop') && (
                          <Text
                            style={optionScoreStyle}
                            fontSize={10}
                          >
                            {`${formatImpliedProbability(option.odds)} implied win prob.`}
                          </Text>
                      )}
                    </Box>
                  </PressableBox>
                </Box>
              );
            })}

            {showAtBadge ? (
              <Box
                position="absolute"
                left="50%"
                alignItems="center"
                justifyContent="center"
                style={[styles.atBadge, atBadgeStyle]}
                pointerEvents="none"
              >
                <Text
                  variant="caption"
                  color="press"
                  fontWeight="900"
                  style={atGlyphStyle}
                >
                  @
                </Text>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  };

  const hasMissingPick = betEntries.some((entry) => {
    if (entry.bet.betType === 'total_tiebreaker') {
      const value = tiebreakerInputs[entry.betKey];
      return !value || !value.trim();
    }
    return !localSelections[entry.betKey];
  });
  const hasGamePickBets = betEntries.some((entry) => ['spread', 'moneyline'].includes(entry.bet.betType));
  const networkLabel = game.network?.trim();
  const shouldShowNetworkLabel =
    !!networkLabel && networkLabel.toUpperCase() !== 'TBD' && networkLabel.toUpperCase() !== 'UNKNOWN';
  const compactTeamNameStyle = { fontSize: isCompactLayout ? 11 : 12 };
  const scoreMarginStyle = { marginTop: 2 };
  const widePropsStyle = isWideLayout ? { marginHorizontal: -8 } : undefined;
  const propsHeaderStyle = { opacity: 1 };
  const propsTitleStyle = { letterSpacing: 1.5 };

  return (
    <Animated.View style={[{ opacity: fadeAnim }]}>
      <Card
        marginBottom="2"
        style={[
          styles.gameCardSurface,
          isGameLocked ? styles.gameCardSurfaceLocked : undefined,
          isFeatured ? styles.featuredCardSurface : undefined,
        ]}
      >
        {isFeatured ? <PaintedTurfTexture intensity="surface" tone="light" /> : null}
        <CardContent paddingHorizontal="2" paddingVertical="2" paddingTop="3" position="relative" zIndex={1}>
          {isFeatured && featuredPropTitle ? (
            <Box marginBottom="4">
              <FeaturedPickMasthead title={featuredPropTitle} description={featuredPropDescription} />
            </Box>
          ) : cardTitle ? (
            <Box marginBottom="3" paddingHorizontal="2">
              <Text variant="title" fontWeight="700" color="text">
                {cardTitle}
              </Text>
            </Box>
          ) : null}

          {!isFeatured ? (
            <Box marginBottom="1" flexDirection="row" alignItems="center" justifyContent="space-between" paddingTop="0">
              <Text
                variant="caption"
                fontWeight="800"
                color="text"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.85}
                style={styles.gameTimeLabel}
              >
                {format(new Date(game.gameTime), "EEEE, MMM d • h:mm a")}
              </Text>
              {isGameLocked ? (
                <Box
                  accessible
                  accessibilityLabel="Picks locked"
                  alignItems="center"
                  backgroundColor="neutral100"
                  borderRadius="full"
                  flexDirection="row"
                  gap="xs"
                  paddingHorizontal="s"
                  paddingVertical="xs"
                >
                  <LockKeyhole size={14} color={tokens.colors.text.secondary} />
                  <Text variant="caption" color="textSecondary" fontSize={12}>
                    PICKS LOCKED
                  </Text>
                </Box>
              ) : shouldShowNetworkLabel ? (
                <Text variant="caption" fontWeight="600" color="mutedForeground">
                  {networkLabel}
                </Text>
              ) : null}
            </Box>
          ) : null}

          {!isFeatured && !hideGameTeams && !hasGamePickBets && (
            <Box marginBottom="3" flexDirection="row" alignItems="center" paddingHorizontal="2">
              <Box flex={1} alignItems="center" paddingRight="2">
                <Box flexDirection="row" alignItems="center" gap={isCompactLayout ? "1" : "2"}>
                  <TeamBadge team={game.awayTeam} size={teamLogoSize} />
                  <Text
                    flex={1}
                    textAlign="center"
                    fontWeight="600"
                    color="text"
                    style={compactTeamNameStyle}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}
                  >
                    {`${game.awayTeam.city} ${game.awayTeam.name}`}
                  </Text>
                </Box>
                {typeof game.awayScore === 'number' && (
                  <Text style={scoreMarginStyle} width="100%" textAlign="center" variant="bodySmall" fontWeight="bold" color="text">
                    {game.awayScore}
                  </Text>
                )}
              </Box>
              <Box marginHorizontal="2" alignItems="center" width={isCompactLayout ? 12 : 16}>
                <Text variant="caption" fontWeight="bold" color={tokens.colors.neutral[400]}>@</Text>
              </Box>
              <Box flex={1} alignItems="center" paddingLeft="2">
                 <Box flexDirection="row" alignItems="center" gap={isCompactLayout ? "1" : "2"}>
                  <TeamBadge team={game.homeTeam} size={teamLogoSize} />
                  <Text
                    flex={1}
                    textAlign="center"
                    fontWeight="600"
                    color="text"
                    style={compactTeamNameStyle}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}
                  >
                    {`${game.homeTeam.city} ${game.homeTeam.name}`}
                  </Text>
                </Box>
                {typeof game.homeScore === 'number' && (
                  <Text style={scoreMarginStyle} width="100%" textAlign="center" variant="bodySmall" fontWeight="bold" color="text">
                    {game.homeScore}
                  </Text>
                )}
              </Box>
            </Box>
          )}

          <Box style={styles.betOptionGroup}>
            {betEntries
              .filter(e => ['spread', 'moneyline'].includes(e.bet.betType))
              .map((entry) => renderBetButtons(
                entry,
                scoringSystem,
                normalizedGameOdds,
                'horizontal',
              ))}
          </Box>

          {hasGamePickBets && isGameStarted ? (
            <GameCardScoreboard
              awayScore={game.awayScore}
              awayLabel={game.awayTeam.abbreviation}
              awayLineScores={game.awayLineScores}
              awayColor={game.awayTeam.primaryColor ?? getTeamPrimaryColor(game.awayTeam.abbreviation)}
              homeScore={game.homeScore}
              homeLabel={game.homeTeam.abbreviation}
              homeLineScores={game.homeLineScores}
              homeColor={game.homeTeam.primaryColor ?? getTeamPrimaryColor(game.homeTeam.abbreviation)}
              isCompleted={game.isCompleted}
              pickState={selectedTeamPickState}
              spreadPick={selectedSpreadPick}
              network={networkLabel}
              period={game.period}
              clock={game.clock}
              situation={game.situation}
              variant="picks"
            />
          ) : null}

          {betEntries.some(e => e.bet.betType === 'total_tiebreaker') && (
            <Box
              marginTop={betEntries.some(e => ['spread', 'moneyline'].includes(e.bet.betType)) ? "6" : "0"}
            >
              <Box>
                {betEntries
                  .filter(e => e.bet.betType === 'total_tiebreaker')
                  .map((entry) => renderBetButtons(entry, scoringSystem, normalizedGameOdds, 'vertical'))}
              </Box>
            </Box>
          )}

          {afterPickContent}

          {!isFeatured && game.id.startsWith('api-football-nfl:') ? (
            <MatchupAnalysisPanel
              gameId={game.id}
              game={{
                awayTeam: game.awayTeam.abbreviation,
                homeTeam: game.homeTeam.abbreviation,
                phase: game.isCompleted ? 'final' : isGameStarted ? 'live' : 'pregame',
                awayScore: game.awayScore ?? null,
                homeScore: game.homeScore ?? null,
                period: game.period ?? null,
                clock: game.clock ?? null,
                situation: game.situation ?? null,
                awayColor: game.awayTeam.primaryColor ?? getTeamPrimaryColor(game.awayTeam.abbreviation),
                homeColor: game.homeTeam.primaryColor ?? getTeamPrimaryColor(game.homeTeam.abbreviation),
                awayOdds: game.awayOdds,
                homeOdds: game.homeOdds,
              }}
              markets={matchupMarkets}
            />
          ) : null}

          {betEntries.some(e => ['total', 'custom_prop'].includes(e.bet.betType)) && (
            isFeatured ? (
              <Box marginTop="4">
                <Box
                  flexDirection={isWideLayout ? "row" : "column"}
                  flexWrap={isWideLayout ? "wrap" : "nowrap"}
                  style={widePropsStyle}
                >
                  {betEntries
                    .filter(e => ['total', 'custom_prop'].includes(e.bet.betType))
                    .map((entry) => (
                      <Box key={entry.betKey} width={isWideLayout ? "50%" : "100%"} paddingHorizontal={isWideLayout ? "2" : "0"}>
                        {renderBetButtons(entry, scoringSystem, normalizedGameOdds, 'vertical')}
                      </Box>
                    ))}
                </Box>
              </Box>
            ) : (
              <Box
                borderRadius="none"
                borderWidth={3}
                borderColor="press"
                padding="3"
                marginTop="6"
                backgroundColor={tokens.colors.ink.paperDark}
              >
                <PressableBox
                  onPress={() => setIsPropsExpanded(!isPropsExpanded)}
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                  style={propsHeaderStyle} // Reset opacity if needed
                >
                  <Box alignItems="center">
                    <Text textAlign="center" variant="button" fontSize={18} style={propsTitleStyle}>
                      {propsSectionTitle}
                    </Text>
                    {!isPropsExpanded && (
                      <Text style={scoreMarginStyle} color="pressMuted" fontSize={12} fontWeight="bold">
                        {game.isCompleted
                          ? `(${propStats.correct}/${propStats.total} correct)`
                          : `(${propStats.made}/${propStats.total} picks made)`}
                      </Text>
                    )}
                  </Box>
                  <Box position="absolute" right={0}>
                    {isPropsExpanded ? (
                      <ChevronUp size={20} color={tokens.colors.ink.press} />
                    ) : (
                      <ChevronDown size={20} color={tokens.colors.ink.press} />
                    )}
                  </Box>
                </PressableBox>
                {isPropsExpanded && (
                  <Box marginTop="4">
                    <Box 
                      flexDirection={isWideLayout ? "row" : "column"} 
                      flexWrap={isWideLayout ? "wrap" : "nowrap"}
                      style={widePropsStyle} // -2 = -8px
                    >
                      {betEntries
                        .filter(e => ['total', 'custom_prop'].includes(e.bet.betType))
                        .map((entry) => (
                          <Box key={entry.betKey} width={isWideLayout ? "50%" : "100%"} paddingHorizontal={isWideLayout ? "2" : "0"}>
                            {renderBetButtons(entry, scoringSystem, normalizedGameOdds, 'vertical')}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )
          )}

          {hasMissingPick && !isGameLocked && (
            <Box style={styles.pickRequiredBanner} accessibilityRole="alert">
              <AlertTriangle size={24} color={tokens.colors.state.warning} />
              <Text style={styles.pickRequiredText}>Pick required</Text>
            </Box>
          )}

        </CardContent>
      </Card>
    </Animated.View>
  );
}
